import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { TescheLogo } from "./TescheLogo";
import { api } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useSiteImage, useContact } from "../../hooks/useSettings";

const WEIGHTS = { bold: "font-bold", extrabold: "font-extrabold", black: "font-black" };

const WA_TESCHE_AR = "السلام عليكم، أود الاستفسار عن إطارات TESCHE";
const WA_TESCHE_EN = "Hello, I'd like to ask about TESCHE tires";

export const BrandsSection = () => {
    const { t, lang, fs } = useLang();
    const isAr = lang === "ar";
    const { waUrl } = useContact();
    const [brands, setBrands] = useState([]);
    const tescheImg = useSiteImage("img_tesche", "/assets/brand/tesche-offroad.png");

    useEffect(() => {
        api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    }, []);

    const featured = brands.find((b) => b.featured);
    const gridBrands = brands.filter((b) => !b.featured);

    return (
        <section id="brands" className="relative py-24 sm:py-32" data-testid="brands-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-12"
                >
                    <p className="font-mono-spec text-xs tracking-[0.3em] text-amber-400 mb-3" dir="ltr">
                        {t("br_eyebrow")}
                    </p>
                    <h2 className={`font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white ${fs("br_title")}`}>
                        {t("br_title")}
                    </h2>
                    <p className={`mt-3 text-slate-400 text-base max-w-2xl ${fs("br_desc")}`}>{t("br_desc")}</p>
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {featured && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="tesche-feature-card"
                            className="col-span-2 row-span-2 relative rounded-3xl overflow-hidden border-2 border-amber-400/50 shadow-[0_0_60px_rgba(250,204,21,0.12)] min-h-[320px] flex"
                        >
                            <img
                                src={tescheImg}
                                alt="TESCHE off-road tires"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1228] via-[#0A1228]/75 to-[#0A1228]/30" />
                            <div className="relative p-6 sm:p-8 flex flex-col justify-end w-full">
                                <TescheLogo className="h-14 sm:h-16 w-auto mb-4 drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]" />
                                <h3 className={`font-display font-extrabold text-white text-xl sm:text-2xl mb-2 ${fs("br_tesche_title")}`}>
                                    {t("br_tesche_title")}
                                </h3>
                                <p className={`text-sm text-slate-300 leading-relaxed mb-5 max-w-md ${fs("br_tesche_desc")}`}>
                                    {t("br_tesche_desc")}
                                </p>
                                <a
                                    href={waUrl(isAr ? WA_TESCHE_AR : WA_TESCHE_EN)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="tesche-whatsapp-button"
                                    className={`inline-flex items-center gap-2 self-start bg-amber-400 text-[#0A1228] text-sm font-extrabold rounded-full px-6 py-3 hover:bg-amber-300 transition-colors duration-300 ${fs("br_tesche_cta")}`}
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    {t("br_tesche_cta")}
                                </a>
                            </div>
                        </motion.div>
                    )}

                    {gridBrands.map((b, i) => (
                        <motion.div
                            key={b.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                            data-testid={`brand-badge-${b.name_en.toLowerCase()}`}
                            className="spotlight-card border border-white/10 rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-1.5 min-h-[96px] hover:border-amber-400/40 hover:-translate-y-1 transition-[border-color,transform] duration-300"
                        >
                            <span
                                className={`text-lg sm:text-xl leading-none ${WEIGHTS[b.weight] || "font-bold"} ${b.italic ? "italic" : ""} ${b.wide ? "tracking-[0.18em]" : ""}`}
                                dir="ltr"
                            >
                                {b.accent_color && (b.accent_pos || "first") === "first" && (
                                    <span style={{ color: b.accent_color }}>{b.name_en.slice(0, b.accent_len)}</span>
                                )}
                                <span style={{ color: b.color }}>
                                    {b.accent_color
                                        ? (b.accent_pos || "first") === "first"
                                            ? b.name_en.slice(b.accent_len)
                                            : b.name_en.slice(0, -b.accent_len)
                                        : b.name_en}
                                </span>
                                {b.accent_color && b.accent_pos === "last" && (
                                    <span style={{ color: b.accent_color }}>{b.name_en.slice(-b.accent_len)}</span>
                                )}
                            </span>
                            <span className="text-[11px] text-slate-500">{b.name_ar}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
